import React, { useEffect, useState } from "react";
import { Accordion, Badge, Button, Col, Form, InputGroup, Row, Table } from "react-bootstrap";
import IsoDatePicker from "../../../components/IsoDatePicker";
import { formatDateDMY } from "../../../utils/dateFormat";
import {
  deleteBookingHotelPayment,
  editBookingHotelPayment,
  followQuoteBookingHotelPayable,
  getBookingHotelPayments,
  recordBookingHotelPayment,
  saveBookingHotelPayable,
} from "../../../services/bookingHotelPaymentApi";
import { DeletedPayments, EditedMark, PaymentRowActions, usePaymentCorrections } from "./PaymentCorrections";

const blankPayment = () => ({
  amount: "",
  paymentDate: new Date().toISOString().slice(0, 10),
  paymentMethod: "UPI",
  referenceNumber: "",
  note: "",
});

export default function HotelVendorPayments({ bookingId }) {
  const [summaries, setSummaries] = useState([]);
  const [payables, setPayables] = useState({});
  const [payments, setPayments] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingHotelId, setSavingHotelId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!bookingId) return;
    setLoading(true);
    setError("");
    getBookingHotelPayments(bookingId)
      .then((items) => {
        const rows = Array.isArray(items) ? items : [];
        setSummaries(rows);
        setPayables(Object.fromEntries(rows.map((row) => [row.hotelId, String(row.totalPayable ?? "")])));
        setPayments(Object.fromEntries(rows.map((row) => [row.hotelId, blankPayment()])));
      })
      .catch((err) => setError(err?.response?.data?.message || "Could not load hotel vendor payments."))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const replaceSummary = (summary) => {
    setSummaries((previous) => previous.map((row) => row.hotelId === summary.hotelId ? summary : row));
    setPayables((previous) => ({ ...previous, [summary.hotelId]: String(summary.totalPayable ?? "") }));
  };

  const savePayable = async (hotelId) => {
    setSavingHotelId(hotelId);
    setError("");
    try {
      replaceSummary(await saveBookingHotelPayable(bookingId, hotelId, Number(payables[hotelId] || 0)));
    } catch (err) {
      setError(err?.response?.data?.message || "Could not update hotel payable.");
    } finally {
      setSavingHotelId(null);
    }
  };

  const followQuote = async (hotelId) => {
    setSavingHotelId(hotelId);
    setError("");
    try {
      replaceSummary(await followQuoteBookingHotelPayable(bookingId, hotelId));
    } catch (err) {
      setError(err?.response?.data?.message || "Could not switch back to the quoted cost.");
    } finally {
      setSavingHotelId(null);
    }
  };

  const recordPayment = async (hotelId) => {
    setSavingHotelId(hotelId);
    setError("");
    try {
      replaceSummary(await recordBookingHotelPayment(bookingId, hotelId, {
        ...payments[hotelId],
        amount: Number(payments[hotelId]?.amount || 0),
      }));
      setPayments((previous) => ({ ...previous, [hotelId]: blankPayment() }));
    } catch (err) {
      setError(err?.response?.data?.message || "Could not record hotel payment.");
    } finally {
      setSavingHotelId(null);
    }
  };

  const corrections = usePaymentCorrections({
    editPayment: (item, change) => editBookingHotelPayment(bookingId, item.hotelId, item.id, change),
    deletePayment: (item, { reason }) => deleteBookingHotelPayment(bookingId, item.hotelId, item.id, reason),
    onChanged: replaceSummary,
  });

  const patchPayment = (hotelId, field, value) =>
    setPayments((previous) => ({ ...previous, [hotelId]: { ...(previous[hotelId] || blankPayment()), [field]: value } }));

  return (
    <Accordion>
      <Accordion.Item eventKey="hotel-vendor-payments">
        <Accordion.Header>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <span className="fw-semibold">Hotel Vendor Payments</span>
            {!!summaries.length && (
              <>
                <Badge bg="secondary">Payable ₹{summaries.reduce((total, hotel) => total + Number(hotel.totalPayable || 0), 0).toLocaleString()}</Badge>
                <Badge bg="success">Paid ₹{summaries.reduce((total, hotel) => total + Number(hotel.totalPaid || 0), 0).toLocaleString()}</Badge>
                <Badge bg={summaries.some((hotel) => Number(hotel.balance || 0) > 0) ? "warning" : "success"} text={summaries.some((hotel) => Number(hotel.balance || 0) > 0) ? "dark" : undefined}>
                  Balance ₹{summaries.reduce((total, hotel) => total + Number(hotel.balance || 0), 0).toLocaleString()}
                </Badge>
              </>
            )}
          </div>
        </Accordion.Header>
        <Accordion.Body>
          {loading && <div className="small text-muted">Loading hotel payment details...</div>}
          {error && <div className="small text-danger mb-2">{error}</div>}
          {!loading && !summaries.length && <div className="small text-muted">No hotels are selected in this booking.</div>}
          <Accordion alwaysOpen>
        {summaries.map((hotel) => {
          const payment = payments[hotel.hotelId] || blankPayment();
          const saving = savingHotelId === hotel.hotelId;
          const quotedPayable = Number(hotel.calculatedPayable || 0);
          const savedPayable = Number(hotel.totalPayable || 0);
          const payableDifference = Math.round((quotedPayable - savedPayable) * 100) / 100;
          const hasPayableMismatch = Math.abs(payableDifference) >= 0.01;
          const quotedBelowPaid = quotedPayable + 0.009 < Number(hotel.totalPaid || 0);
          return (
            <Accordion.Item eventKey={String(hotel.hotelId)} key={hotel.hotelId}>
              <Accordion.Header>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <span>{hotel.hotelName} ({hotel.hotelCode})</span>
                  <Badge bg="secondary">Payable ₹{Number(hotel.totalPayable || 0).toLocaleString()}</Badge>
                  <Badge bg="success">Paid ₹{Number(hotel.totalPaid || 0).toLocaleString()}</Badge>
                  <Badge bg={Number(hotel.balance || 0) > 0 ? "warning" : "success"} text={Number(hotel.balance || 0) > 0 ? "dark" : undefined}>
                    Balance ₹{Number(hotel.balance || 0).toLocaleString()}
                  </Badge>
                  {hotel.manualOverride && <Badge bg="info" text="dark">Manual amount</Badge>}
                  {hotel.notOnBooking && <Badge bg="danger">Not on this booking</Badge>}
                </div>
              </Accordion.Header>
              <Accordion.Body>
                {hotel.notOnBooking && (
                  <div className="border border-danger rounded p-2 mb-3 small text-danger">
                    This hotel is no longer on the booking, but payments are recorded against it. Check whether this money
                    really went to this hotel; edit or delete the entries below if they are wrong. New payments can only be
                    recorded for hotels on the booking.
                  </div>
                )}
                <div className="bg-light border rounded p-2 mb-3 small">
                  <div className="fw-semibold">{hotel.city || "-"} - {hotel.hotelName}</div>
                  <div>UPI: {hotel.upiNumber || "Not provided"} {hotel.upiScannerUrl && <>· <a href={hotel.upiScannerUrl} target="_blank" rel="noreferrer">View UPI scanner</a></>}</div>
                  <div>Bank: {hotel.bankAccountHolder || "-"} · {hotel.bankAccountNumber || "-"} · IFSC {hotel.bankIfsc || "-"}</div>
                </div>
                {!hotel.notOnBooking && hasPayableMismatch && (
                  <div className="border rounded p-2 mb-3 small bg-light">
                    <div>
                      Quoted cost ₹{quotedPayable.toLocaleString()} · Saved payable ₹{savedPayable.toLocaleString()} ·
                      Difference {payableDifference > 0 ? "+" : "-"}₹{Math.abs(payableDifference).toLocaleString()}
                    </div>
                    <div className="text-muted">
                      {hotel.manualOverride
                        ? "This hotel is on a manually entered amount, so quote changes will not touch it."
                        : "The saved payable differs from the quoted cost."}
                    </div>
                    {quotedBelowPaid && (
                      <div className="text-danger">The quoted cost is below what has already been paid to this hotel.</div>
                    )}
                    <Button
                      className="mt-1"
                      size="sm"
                      variant="outline-secondary"
                      disabled={saving || quotedBelowPaid}
                      onClick={() => followQuote(hotel.hotelId)}
                    >
                      Use quoted cost ₹{quotedPayable.toLocaleString()}
                    </Button>
                  </div>
                )}
                {!hotel.notOnBooking && (<>
                <Row className="g-2 mb-3">
                  <Col md={5}>
                    <Form.Label>Total Payable</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>₹</InputGroup.Text>
                      <Form.Control type="number" min="0" step="0.01" value={payables[hotel.hotelId] ?? ""} onChange={(e) => setPayables((old) => ({ ...old, [hotel.hotelId]: e.target.value }))} />
                      <Button variant="outline-primary" disabled={saving} onClick={() => savePayable(hotel.hotelId)}>Save Payable</Button>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Enter what you will actually pay this hotel. A saved amount stays put even if the booking is revised.
                    </Form.Text>
                  </Col>
                </Row>
                <div className="fw-semibold mb-2">Record Installment</div>
                <Row className="g-2 mb-3">
                  <Col md={3}><Form.Label>Amount *</Form.Label><InputGroup><InputGroup.Text>₹</InputGroup.Text><Form.Control type="number" min="0.01" step="0.01" value={payment.amount} onChange={(e) => patchPayment(hotel.hotelId, "amount", e.target.value)} /></InputGroup></Col>
                  <Col md={3}><Form.Label>Payment Date *</Form.Label><IsoDatePicker value={payment.paymentDate} onChange={(value) => patchPayment(hotel.hotelId, "paymentDate", value)} /></Col>
                  <Col md={3}><Form.Label>Method *</Form.Label><Form.Select value={payment.paymentMethod} onChange={(e) => patchPayment(hotel.hotelId, "paymentMethod", e.target.value)}><option value="UPI">UPI</option><option value="BANK_TRANSFER">Bank Transfer</option><option value="CASH">Cash</option><option value="OTHER">Other</option></Form.Select></Col>
                  <Col md={3}><Form.Label>Reference / UTR</Form.Label><Form.Control value={payment.referenceNumber} onChange={(e) => patchPayment(hotel.hotelId, "referenceNumber", e.target.value)} /></Col>
                  <Col md={9}><Form.Label>Payment Note *</Form.Label><Form.Control value={payment.note} onChange={(e) => patchPayment(hotel.hotelId, "note", e.target.value)} /></Col>
                  <Col md={3} className="d-flex align-items-end"><Button className="w-100" disabled={saving || !Number(payment.amount) || !payment.note.trim()} onClick={() => recordPayment(hotel.hotelId)}>Record Payment</Button></Col>
                </Row>
                </>)}
                <div className="table-responsive">
                  <Table bordered size="sm" className="mb-0">
                    <thead><tr><th style={{ width: 44 }}>#</th><th>Date</th><th>Amount</th><th>Method</th><th>Reference</th><th>Note</th><th style={{ width: 84 }}>Actions</th></tr></thead>
                    <tbody>
                      {(hotel.transactions || []).map((item, index) => <tr key={item.id}><td className="small fw-semibold">{index + 1}</td><td>{formatDateDMY(item.paymentDate)}</td><td>₹{Number(item.amount || 0).toLocaleString()}<EditedMark payment={item} /></td><td>{item.paymentMethod?.replaceAll("_", " ")}</td><td>{item.referenceNumber || "-"}</td><td>{item.note}</td><td><PaymentRowActions payment={{ ...item, hotelId: hotel.hotelId }} corrections={corrections} disabled={savingHotelId === hotel.hotelId} /></td></tr>)}
                      {!hotel.transactions?.length && <tr><td colSpan={7} className="text-center text-muted">No hotel payments recorded.</td></tr>}
                    </tbody>
                  </Table>
                </div>
                <DeletedPayments payments={hotel.deletedTransactions} />
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
          </Accordion>
          {corrections.modal}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
}
